import { LitElement, type PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { createContext } from '@lit/context'
import { provideOnBody } from 'components/shared/context/body-context.js'

export interface RolePermissions {
  createOrders: boolean
  loggedIn: boolean
}

export const DEFAULT_ROLE_PERMISSIONS: RolePermissions = {
  createOrders: true,
  loggedIn: false,
}

export const RolePermissionsContext = createContext<RolePermissions>(Symbol('csc-role-permissions-context'))

/**
 * Provides role permissions to every component on the page via `provideOnBody`, so this element
 * must be used standalone (e.g. `<csc-role-provider></csc-role-provider>`) and never as a wrapper.
 * It renders an empty shadow root with no `<slot>`, so any children placed inside it will exist in
 * the DOM but never be displayed.
**/
@customElement('csc-role-provider')
export class CSCRoleProvider extends LitElement {
  @property({ type: Boolean, attribute: 'disable-orders' })
  disableOrders = false

  @property({ type: Boolean, attribute: 'logged-in' })
  loggedIn = false

  @provideOnBody({ context: RolePermissionsContext })
  permissions: RolePermissions = { ...DEFAULT_ROLE_PERMISSIONS }

  protected override willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('disableOrders')) {
      this.permissions = { ...this.permissions, createOrders: !this.disableOrders }
    }

    if (changedProperties.has('loggedIn')) {
      this.permissions = { ...this.permissions, loggedIn: this.loggedIn }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-role-provider': CSCRoleProvider
  }
}
